/**
 * Inbox Hooks
 *
 * Custom hooks for inbox operations using React Query for caching and deduplication.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { inboxService } from '../../../api/inbox.service';
import type {
  InboxMessageSanitized,
  InboxListQuery,
  InboxView,
  InboxMessageType,
  InboxMessagePriority,
} from '../../../types/inbox.types';
import { getErrorMessage } from '../../../lib/utils/errors';

// ============================================================================
// QUERY KEYS
// ============================================================================

export const INBOX_QUERY_KEY = 'inbox';
export const INBOX_UNREAD_COUNT_QUERY_KEY = 'inboxUnreadCount';

// Polling interval for inbox (60 seconds)
const INBOX_POLLING_INTERVAL = 60000;

// ============================================================================
// INBOX LIST HOOK
// ============================================================================

export interface UseInboxOptions extends InboxListQuery {
  /** View type (inbox, archived, deleted) - defaults to inbox */
  view?: InboxView;
}

export interface UseInboxResult {
  messages: InboxMessageSanitized[];
  isLoading: boolean;
  error: string | null;
  hasMore: boolean;
  cursor?: string;
  unreadCount: number;
  refresh: () => void;
  loadMore: () => Promise<void>;
  markAsRead: (messageId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  archiveMessage: (messageId: string) => Promise<void>;
  unarchiveMessage: (messageId: string) => Promise<void>;
  softDeleteMessage: (messageId: string) => Promise<void>;
  restoreMessage: (messageId: string) => Promise<void>;
  permanentDeleteMessage: (messageId: string) => Promise<void>;
}

export function useInbox(options?: UseInboxOptions): UseInboxResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const tenantId = user?.tenantId || '';
  const userId = user?.userId || '';
  const view = options?.view || 'inbox';

  const enabled = !!tenantId && !!userId;

  // Build query key that includes all filter options
  const queryKeyParams = [
    INBOX_QUERY_KEY,
    tenantId,
    userId,
    view,
    options?.unreadOnly,
    options?.type,
    options?.priority,
    options?.dateStart,
    options?.dateEnd,
    options?.limit,
  ];

  // ============================================================================
  // QUERIES
  // ============================================================================

  /**
   * Fetch messages query
   */
  const {
    data: messagesData,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery<{ items: InboxMessageSanitized[]; hasMore: boolean; cursor?: string }, Error>({
    queryKey: queryKeyParams,
    queryFn: async () => {
      const result = await inboxService.listMessages(tenantId, userId, {
        view,
        unreadOnly: options?.unreadOnly,
        type: options?.type,
        priority: options?.priority,
        dateStart: options?.dateStart,
        dateEnd: options?.dateEnd,
        limit: options?.limit,
        cursor: undefined, // Always start from beginning for the main query
      });
      return result;
    },
    enabled,
    staleTime: 10000, // 10 seconds stale time
    refetchInterval: (query) => {
      if (query.state.error && (query.state.error as { response?: { status?: number } }).response?.status === 401) return false;
      return INBOX_POLLING_INTERVAL;
    },
  });

  /**
   * Fetch unread count query
   */
  const { data: unreadCount = 0 } = useQuery<number, Error>({
    queryKey: [INBOX_UNREAD_COUNT_QUERY_KEY, tenantId, userId],
    queryFn: () => inboxService.getUnreadCount(tenantId, userId),
    enabled,
    staleTime: 10000, // 10 seconds stale time
    refetchInterval: (query) => {
      if (query.state.error && (query.state.error as { response?: { status?: number } }).response?.status === 401) return false;
      return INBOX_POLLING_INTERVAL;
    },
  });

  // ============================================================================
  // MUTATIONS
  // ============================================================================

  /**
   * Mark as read mutation
   */
  const markAsReadMutation = useMutation({
    mutationFn: (messageId: string) =>
      inboxService.markAsRead(tenantId, userId, messageId),
    onSuccess: (updatedMessage) => {
      // Update the messages cache
      queryClient.setQueryData<{ items: InboxMessageSanitized[]; hasMore: boolean; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((msg) =>
              msg.messageId === updatedMessage.messageId ? updatedMessage : msg
            ),
          };
        }
      );
      // Invalidate unread count
      queryClient.invalidateQueries({
        queryKey: [INBOX_UNREAD_COUNT_QUERY_KEY, tenantId, userId],
      });
    },
  });

  /**
   * Mark all as read mutation
   */
  const markAllAsReadMutation = useMutation({
    mutationFn: () => inboxService.markAllAsRead(tenantId, userId),
    onSuccess: () => {
      // Update all messages to read in cache
      queryClient.setQueryData<{ items: InboxMessageSanitized[]; hasMore: boolean; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((msg) => ({ ...msg, isRead: true })),
          };
        }
      );
      // Invalidate unread count
      queryClient.invalidateQueries({
        queryKey: [INBOX_UNREAD_COUNT_QUERY_KEY, tenantId, userId],
      });
    },
  });

  /**
   * Archive message mutation
   */
  const archiveMessageMutation = useMutation({
    mutationFn: (messageId: string) =>
      inboxService.archiveMessage(tenantId, userId, messageId),
    onSuccess: (_, messageId) => {
      // Get the archived message to check if it was unread
      const currentData = queryClient.getQueryData<{ items: InboxMessageSanitized[]; hasMore: boolean; cursor?: string }>(queryKeyParams);
      const archivedMessage = currentData?.items.find((msg) => msg.messageId === messageId);

      // Remove from messages cache (it moves to archived view)
      queryClient.setQueryData<{ items: InboxMessageSanitized[]; hasMore: boolean; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((msg) => msg.messageId !== messageId),
          };
        }
      );

      // Invalidate all inbox queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: [INBOX_QUERY_KEY, tenantId, userId],
        exact: false,
      });

      // Invalidate unread count if message was unread
      if (archivedMessage && !archivedMessage.isRead) {
        queryClient.invalidateQueries({
          queryKey: [INBOX_UNREAD_COUNT_QUERY_KEY, tenantId, userId],
        });
      }
    },
  });

  /**
   * Unarchive message mutation
   */
  const unarchiveMessageMutation = useMutation({
    mutationFn: (messageId: string) =>
      inboxService.unarchiveMessage(tenantId, userId, messageId),
    onSuccess: (_, messageId) => {
      // Remove from current view (archived) as it moves back to inbox
      queryClient.setQueryData<{ items: InboxMessageSanitized[]; hasMore: boolean; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((msg) => msg.messageId !== messageId),
          };
        }
      );

      // Invalidate all inbox queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: [INBOX_QUERY_KEY, tenantId, userId],
        exact: false,
      });
    },
  });

  /**
   * Soft delete message mutation
   */
  const softDeleteMessageMutation = useMutation({
    mutationFn: (messageId: string) =>
      inboxService.softDeleteMessage(tenantId, userId, messageId),
    onSuccess: (_, messageId) => {
      // Get the deleted message to check if it was unread
      const currentData = queryClient.getQueryData<{ items: InboxMessageSanitized[]; hasMore: boolean; cursor?: string }>(queryKeyParams);
      const deletedMessage = currentData?.items.find((msg) => msg.messageId === messageId);

      // Remove from current view as it moves to deleted view
      queryClient.setQueryData<{ items: InboxMessageSanitized[]; hasMore: boolean; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((msg) => msg.messageId !== messageId),
          };
        }
      );

      // Invalidate all inbox queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: [INBOX_QUERY_KEY, tenantId, userId],
        exact: false,
      });

      // Invalidate unread count if message was unread
      if (deletedMessage && !deletedMessage.isRead) {
        queryClient.invalidateQueries({
          queryKey: [INBOX_UNREAD_COUNT_QUERY_KEY, tenantId, userId],
        });
      }
    },
  });

  /**
   * Restore message mutation
   */
  const restoreMessageMutation = useMutation({
    mutationFn: (messageId: string) =>
      inboxService.restoreMessage(tenantId, userId, messageId),
    onSuccess: (_, messageId) => {
      // Remove from current view (deleted) as it moves back to inbox
      queryClient.setQueryData<{ items: InboxMessageSanitized[]; hasMore: boolean; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((msg) => msg.messageId !== messageId),
          };
        }
      );

      // Invalidate all inbox queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: [INBOX_QUERY_KEY, tenantId, userId],
        exact: false,
      });
    },
  });

  /**
   * Permanent delete message mutation
   */
  const permanentDeleteMessageMutation = useMutation({
    mutationFn: (messageId: string) =>
      inboxService.permanentDeleteMessage(tenantId, userId, messageId),
    onSuccess: (_, messageId) => {
      // Remove from current view permanently
      queryClient.setQueryData<{ items: InboxMessageSanitized[]; hasMore: boolean; cursor?: string }>(
        queryKeyParams,
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.filter((msg) => msg.messageId !== messageId),
          };
        }
      );
    },
  });

  // ============================================================================
  // LOAD MORE (for pagination)
  // ============================================================================

  const loadMore = async () => {
    if (!messagesData?.hasMore || !messagesData?.cursor) return;

    const moreData = await inboxService.listMessages(tenantId, userId, {
      view,
      unreadOnly: options?.unreadOnly,
      type: options?.type,
      priority: options?.priority,
      dateStart: options?.dateStart,
      dateEnd: options?.dateEnd,
      limit: options?.limit,
      cursor: messagesData.cursor,
    });

    // Append to existing data
    queryClient.setQueryData<{ items: InboxMessageSanitized[]; hasMore: boolean; cursor?: string }>(
      queryKeyParams,
      (old) => {
        if (!old) return moreData;
        return {
          items: [...old.items, ...moreData.items],
          hasMore: moreData.hasMore,
          cursor: moreData.cursor,
        };
      }
    );
  };

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    messages: messagesData?.items || [],
    isLoading,
    error: queryError ? getErrorMessage(queryError) : null,
    hasMore: messagesData?.hasMore || false,
    cursor: messagesData?.cursor,
    unreadCount,
    refresh: () => {
      refetch();
      queryClient.invalidateQueries({
        queryKey: [INBOX_UNREAD_COUNT_QUERY_KEY, tenantId, userId],
      });
    },
    loadMore,
    markAsRead: async (messageId: string) => {
      await markAsReadMutation.mutateAsync(messageId);
    },
    markAllAsRead: async () => {
      await markAllAsReadMutation.mutateAsync();
    },
    archiveMessage: async (messageId: string) => {
      await archiveMessageMutation.mutateAsync(messageId);
    },
    unarchiveMessage: async (messageId: string) => {
      await unarchiveMessageMutation.mutateAsync(messageId);
    },
    softDeleteMessage: async (messageId: string) => {
      await softDeleteMessageMutation.mutateAsync(messageId);
    },
    restoreMessage: async (messageId: string) => {
      await restoreMessageMutation.mutateAsync(messageId);
    },
    permanentDeleteMessage: async (messageId: string) => {
      await permanentDeleteMessageMutation.mutateAsync(messageId);
    },
  };
}

// ============================================================================
// UNREAD COUNT HOOK (standalone for components that only need the count)
// ============================================================================

export interface UseUnreadCountResult {
  unreadCount: number;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

export function useUnreadCount(): UseUnreadCountResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const tenantId = user?.tenantId || '';
  const userId = user?.userId || '';

  const enabled = !!tenantId && !!userId;

  const { data: unreadCount = 0, isLoading, refetch } = useQuery<number, Error>({
    queryKey: [INBOX_UNREAD_COUNT_QUERY_KEY, tenantId, userId],
    queryFn: () => inboxService.getUnreadCount(tenantId, userId),
    enabled,
    staleTime: 10000, // 10 seconds stale time
    refetchInterval: (query) => {
      if (query.state.error && (query.state.error as { response?: { status?: number } }).response?.status === 401) return false;
      return INBOX_POLLING_INTERVAL;
    },
  });

  return {
    unreadCount,
    isLoading,
    refresh: async () => {
      await refetch();
    },
  };
}
